import * as THREE from 'three';
import { Weapon, WeaponConfig } from './Weapon';
import { EventBus } from '../core/EventBus';

const RAILGUN_CONFIG: WeaponConfig = {
  name: 'Railgun',
  damage: 150,
  fireRate: 0.5, // Slow - 0.5 shots per second (2 second cooldown)
  projectileSpeed: 0, // Instant hit (hitscan)
  ammoMax: 10,
  reloadTime: 4,
  spread: 0, // Perfect accuracy
  automatic: false, // Single shot
  projectileColor: new THREE.Color(0x00ff88),
  projectileSize: 0.3,
};

// Hitscan callback for checking what we hit
export type HitscanCallback = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  maxDistance: number
) => { hit: boolean; target?: THREE.Object3D; point?: THREE.Vector3; distance?: number } | null;

export class Railgun extends Weapon {
  private scene: THREE.Scene;
  private hitscanCallback: HitscanCallback | null = null;
  private readonly MAX_RANGE = 500;

  // Visual trail
  private trailGeometry: THREE.BufferGeometry | null = null;
  private trailMesh: THREE.Line | null = null;
  private trailStartTime: number = 0;
  private readonly TRAIL_DURATION = 0.3; // seconds

  constructor(scene: THREE.Scene) {
    super(RAILGUN_CONFIG);
    this.scene = scene;
  }

  public setHitscanCallback(callback: HitscanCallback): void {
    this.hitscanCallback = callback;
  }

  protected createProjectile(origin: THREE.Vector3, direction: THREE.Vector3): void {
    // Railgun is hitscan - instant hit
    const endPoint = origin.clone().add(direction.clone().multiplyScalar(this.MAX_RANGE));
    let hitPoint = endPoint;
    let hitTarget: THREE.Object3D | null = null;

    // Check for hits
    if (this.hitscanCallback) {
      const result = this.hitscanCallback(origin, direction, this.MAX_RANGE);
      if (result && result.hit && result.point) {
        hitPoint = result.point;
        hitTarget = result.target || null;
      }
    }

    // Create visual trail
    this.createTrail(origin, hitPoint);

    // Apply damage if we hit something
    if (hitTarget && hitTarget.userData.entity) {
      const entity = hitTarget.userData.entity;
      if (typeof entity.takeDamage === 'function') {
        entity.takeDamage(this.getEffectiveDamage());
      }
    }

    // Emit hit event
    EventBus.emit('projectile:hit', {
      position: {
        x: hitPoint.x,
        y: hitPoint.y,
        z: hitPoint.z,
      },
      targetType: hitTarget ? 'enemy' : 'world',
    });
  }

  private createTrail(start: THREE.Vector3, end: THREE.Vector3): void {
    // Remove old trail
    this.removeTrail();

    // Create line geometry
    const points = [start, end];
    this.trailGeometry = new THREE.BufferGeometry().setFromPoints(points);

    // Create glowing material
    const material = new THREE.LineBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 1,
      linewidth: 3,
    });

    this.trailMesh = new THREE.Line(this.trailGeometry, material);
    this.scene.add(this.trailMesh);
    this.trailStartTime = performance.now() / 1000;

    // Add glow effect - thicker secondary line
    const glowMaterial = new THREE.LineBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.5,
    });
    const glowLine = new THREE.Line(this.trailGeometry, glowMaterial);
    this.trailMesh.add(glowLine);
  }

  private removeTrail(): void {
    if (this.trailMesh) {
      this.scene.remove(this.trailMesh);
      this.trailMesh.geometry.dispose();
      (this.trailMesh.material as THREE.Material).dispose();
      this.trailMesh = null;
    }
    this.trailGeometry = null;
  }

  public update(): void {
    super.update();

    // Fade out trail
    if (this.trailMesh) {
      const elapsed = performance.now() / 1000 - this.trailStartTime;
      if (elapsed >= this.TRAIL_DURATION) {
        this.removeTrail();
      } else {
        const opacity = 1 - elapsed / this.TRAIL_DURATION;
        (this.trailMesh.material as THREE.LineBasicMaterial).opacity = opacity;
        if (this.trailMesh.children[0]) {
          ((this.trailMesh.children[0] as THREE.Line).material as THREE.LineBasicMaterial).opacity =
            opacity * 0.5;
        }
      }
    }
  }

  public destroy(): void {
    this.removeTrail();
  }
}
